import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ChevronLeft,
  Zap,
  Bot,
  ShieldCheck,
  Users,
  TrendingUp,
  Activity,
  Award,
  AlertTriangle,
  Pause,
  Play,
  X,
} from "lucide-react";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { AreaChart } from "../components/dashboard/Charts";
import CopyModal from "../components/dashboard/CopyModal";
import { getTrader, riskBadge } from "../copyTradingMock";
import { useCopies } from "../hooks/useCopies";

const formatMoney = (n) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const compactNum = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
};

const StatBox = ({ label, value, sub, accent }) => (
  <div className="bb-card rounded-2xl p-4 lg:p-5">
    <div className="text-[11px] text-white/55 mb-1.5">{label}</div>
    <div className={`font-display font-bold text-[20px] lg:text-[22px] tabular-nums ${accent || "text-white"}`}>
      {value}
    </div>
    {sub && <div className="text-[10px] text-white/40 mt-1">{sub}</div>}
  </div>
);

const CopyTraderDetail = () => {
  const { traderId } = useParams();
  const trader = getTrader(traderId);
  const [modalOpen, setModalOpen] = useState(false);
  const { copies, pauseCopy, stopCopy } = useCopies();
  const myCopy = copies.find((c) => c.traderId === traderId);

  if (!trader) return <Navigate to="/dashboard/copy-trading" replace />;

  const isAI = trader.type === "AI Bot";
  const rb = riskBadge(trader.risk);

  return (
    <DashboardLayout title={trader.name} subtitle="Trader profile">
      <Link
        to="/dashboard/copy-trading"
        className="inline-flex items-center gap-1.5 text-[13px] text-white/55 hover:text-white mb-4 transition-colors"
      >
        <ChevronLeft size={16} /> Back to traders
      </Link>

      {/* Hero */}
      <div className="bb-card rounded-3xl p-5 lg:p-8 mb-5 relative overflow-hidden">
        <div
          className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-30"
          style={{ background: trader.color }}
        ></div>
        <div className="relative flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">
          <div
            className="w-20 h-20 lg:w-24 lg:h-24 rounded-3xl flex items-center justify-center text-[28px] lg:text-[32px] font-bold text-white shrink-0"
            style={{ background: `linear-gradient(135deg, ${trader.color}, ${trader.accent})` }}
          >
            {trader.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="font-display font-bold text-[28px] lg:text-[34px] text-white leading-none">
                {trader.name}
              </h1>
              {isAI ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#1c3df5]/15 border border-[#1c3df5]/30 text-[10px] uppercase tracking-wider font-semibold text-[#7d8eff]">
                  <Bot size={11} /> AI
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-[10px] uppercase tracking-wider font-semibold text-emerald-400">
                  <ShieldCheck size={11} /> Verified
                </span>
              )}
              <span
                className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${rb.color} ${rb.bg} ${rb.border}`}
              >
                {trader.risk} risk
              </span>
            </div>
            <div className="text-[13px] text-white/50 mb-3">{trader.handle}</div>
            <p className="text-white/65 text-[14px] leading-relaxed max-w-2xl mb-5">{trader.bio}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-6">
              <div>
                <div className="text-[11px] text-white/45 mb-0.5">Total return</div>
                <div className="font-display font-bold text-[26px] text-emerald-400 tabular-nums leading-none">
                  +{trader.totalReturn}%
                </div>
              </div>
              <div>
                <div className="text-[11px] text-white/45 mb-0.5">Followers</div>
                <div className="font-display font-bold text-[20px] text-white tabular-nums leading-none">
                  {compactNum(trader.followers)}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-white/45 mb-0.5">AUM</div>
                <div className="font-display font-bold text-[20px] text-white tabular-nums leading-none">
                  ${compactNum(trader.aum)}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-white/45 mb-0.5">Active for</div>
                <div className="font-display font-bold text-[20px] text-white tabular-nums leading-none">
                  {trader.age}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-auto lg:min-w-[200px]">
            {myCopy ? (
              <div className="bb-card rounded-2xl p-4 border border-emerald-500/30 bg-emerald-500/[0.04]">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-emerald-400">
                    Copying
                  </span>
                </div>
                <div className="text-[11px] text-white/55">Current value</div>
                <div className="font-display font-bold text-[20px] text-white tabular-nums">
                  ${formatMoney(myCopy.currentValue)}
                </div>
                <div
                  className={`text-[12px] tabular-nums font-medium mb-3 ${
                    myCopy.currentValue >= myCopy.allocated ? "text-emerald-400" : "text-rose-400"
                  }`}
                >
                  {myCopy.currentValue >= myCopy.allocated ? "+" : ""}${formatMoney(myCopy.currentValue - myCopy.allocated)}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => pauseCopy(myCopy.id)}
                    className="flex-1 py-2 rounded-lg border border-white/10 hover:border-white/25 text-white/70 hover:text-white text-[12px] font-medium inline-flex items-center justify-center gap-1.5"
                  >
                    {myCopy.status === "active" ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}
                  </button>
                  <button
                    onClick={() => stopCopy(myCopy.id)}
                    className="py-2 px-3 rounded-lg border border-white/10 hover:border-rose-400 hover:bg-rose-500/10 text-white/55 hover:text-rose-400 text-[12px] inline-flex items-center justify-center"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setModalOpen(true)}
                className="w-full py-3.5 rounded-xl bb-blue text-white font-semibold inline-flex items-center justify-center gap-2 transition-all"
              >
                <Zap size={16} /> Copy this trader
              </button>
            )}
            <div className="text-[10px] text-white/40 text-center mt-2">
              Min copy: ${trader.minCopy}
            </div>
          </div>
        </div>
      </div>

      {/* Equity chart */}
      <div className="bb-card rounded-2xl p-5 lg:p-6 mb-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[12px] text-white/55 mb-1">Equity curve (last 6 months)</div>
            <div className="flex items-baseline gap-3">
              <div className="font-display font-bold text-[22px] text-white tabular-nums">
                ${formatMoney(trader.equity[trader.equity.length - 1])}
              </div>
              <div className="text-[13px] text-emerald-400 font-medium tabular-nums">
                +{trader.totalReturn}%
              </div>
            </div>
          </div>
          <div className="flex gap-1 bg-white/[0.04] rounded-lg p-1 border border-white/8">
            {["1M", "3M", "6M", "All"].map((r, i) => (
              <button
                key={r}
                className={`px-2.5 py-1 text-[11px] rounded-md transition-colors ${
                  i === 2 ? "bg-white text-[#07080d] font-medium" : "text-white/55 hover:text-white"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        <AreaChart data={trader.equity} height={240} />
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-5">
        <StatBox label="Win rate" value={`${trader.winRate}%`} sub={`Across ${trader.totalTrades} trades`} accent="text-emerald-400" />
        <StatBox label="Profit factor" value={trader.profitFactor.toFixed(2)} sub="Wins / losses" />
        <StatBox label="Max drawdown" value={`${trader.maxDrawdown}%`} accent="text-rose-400" />
        <StatBox label="Sharpe ratio" value={trader.sharpe.toFixed(2)} sub="Risk-adjusted return" />
        <StatBox label="Avg trade" value={`$${trader.avgTrade.toFixed(2)}`} />
        <StatBox label="Total trades" value={trader.totalTrades.toLocaleString()} />
        <StatBox label="Monthly return" value={`+${trader.monthlyReturn}%`} accent="text-emerald-400" />
        <StatBox label="Risk score" value={`${trader.riskScore}/10`} />
      </div>

      {/* Assets + Recent trades */}
      <div className="grid lg:grid-cols-3 gap-4 lg:gap-5 mb-5">
        <div className="bb-card rounded-2xl p-5 lg:p-6">
          <div className="font-display font-semibold text-[15px] text-white mb-4">Asset allocation</div>
          <div className="space-y-3">
            {Object.entries(trader.assets).map(([asset, pct]) => (
              <div key={asset}>
                <div className="flex justify-between text-[12px] mb-1.5">
                  <span className="text-white/70">{asset}</span>
                  <span className="text-white tabular-nums font-medium">{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, background: trader.color }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-5 border-t border-white/5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/8 flex items-center justify-center">
              <Award size={15} className="text-amber-400" />
            </div>
            <div className="text-[12px] text-white/55">
              Top {Math.floor((1 - trader.riskScore / 12) * 20) + 1}% performer on Northwind
            </div>
          </div>
        </div>

        <div className="bb-card rounded-2xl p-5 lg:p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold text-[15px] text-white">Recent trades</div>
            <div className="flex items-center gap-1.5 text-[11px] text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> Live
            </div>
          </div>
          <div className="space-y-1">
            {trader.recentTrades.map((t, i) => {
              const profit = t.pnl >= 0;
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.03] transition-colors"
                >
                  <div
                    className={`w-1 h-9 rounded-full ${
                      t.type === "BUY" ? "bg-emerald-500" : "bg-rose-500"
                    }`}
                  ></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] text-white font-medium">{t.pair}</span>
                      <span
                        className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                          t.type === "BUY"
                            ? "text-emerald-400 bg-emerald-400/10"
                            : "text-rose-400 bg-rose-400/10"
                        }`}
                      >
                        {t.type}
                      </span>
                    </div>
                    <div className="text-[11px] text-white/45 mt-0.5">{t.time}</div>
                  </div>
                  <div
                    className={`text-[13px] tabular-nums font-semibold ${
                      profit ? "text-emerald-400" : "text-rose-400"
                    }`}
                  >
                    {profit ? "+" : ""}${Math.abs(t.pnl).toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bb-card rounded-2xl p-5 flex items-start gap-3">
        <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
        <div className="text-[12px] text-white/55 leading-relaxed">
          Past performance shown for {trader.name} is historical and does not guarantee future results. Copy trading carries risk and you may lose capital. Demo mode — no real funds are moved on this platform.
        </div>
      </div>

      <CopyModal
        trader={trader}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </DashboardLayout>
  );
};

export default CopyTraderDetail;
